import { useEffect, useRef } from 'react';

export interface ModalEventHandlers {
  onOpenBasePay: () => void;
  onOpenTax: () => void;
  onOpenAddPay: () => void;
  onPayItemsLoaded: (assignedPayTypeIds: string[]) => void;
  onOpenEditPay: (item: unknown) => void;
  onOpenStatHoliday: (data: unknown) => void;
  onOpenTimeOff: (data: unknown) => void;
  onOpenAddDeduction: () => void;
  onOpenDentalCode: (current: unknown) => void;
  onUnassignDeduction: (item: { id?: string | number } | null) => void;
  onOpenPersonalInfo: () => void;
  onOpenEmergency: () => void;
  onOpenEmployment: (locations: unknown[]) => void;
  onOpenPaymentMethod: () => void;
}

/**
 * Wires 14 window.novala:* custom events to the provided handlers.
 * Handlers are kept in a ref so re-renders don't re-register listeners.
 */
export function useModalEvents(handlers: ModalEventHandlers): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const h = handlersRef;

    const handleOpenBasePay = (): void => h.current.onOpenBasePay();
    const handleOpenTax = (): void => h.current.onOpenTax();
    const handleOpenAddPay = (): void => h.current.onOpenAddPay();
    const handlePayItemsLoaded = (e: Event): void => {
      const ids = ((e as CustomEvent).detail && (e as CustomEvent).detail.assignedPayTypeIds) || [];
      h.current.onPayItemsLoaded(ids);
    };
    const handleOpenEditPay = (e: Event): void => {
      h.current.onOpenEditPay((e as CustomEvent).detail || null);
    };
    const handleOpenStatHoliday = (e: Event): void => {
      const payload = (e as CustomEvent).detail || null;
      h.current.onOpenStatHoliday(payload && payload.data ? payload.data : null);
    };
    const handleOpenTimeOff = (e: Event): void => {
      const payload = (e as CustomEvent).detail || null;
      h.current.onOpenTimeOff(payload && payload.data ? payload.data : null);
    };
    const handleOpenAddDeduction = (): void => h.current.onOpenAddDeduction();
    const handleOpenDentalCode = (e: Event): void => {
      const payload = (e as CustomEvent).detail || null;
      h.current.onOpenDentalCode(payload && payload.current ? payload.current : null);
    };
    const handleUnassignDeduction = (e: Event): void => {
      h.current.onUnassignDeduction((e as CustomEvent).detail || null);
    };
    const handleOpenPersonalInfo = (): void => h.current.onOpenPersonalInfo();
    const handleOpenEmergency = (): void => h.current.onOpenEmergency();
    const handleOpenEmployment = (e: Event): void => {
      const payload = (e as CustomEvent).detail || null;
      h.current.onOpenEmployment(payload && Array.isArray(payload.locations) ? payload.locations : []);
    };
    const handleOpenPaymentMethod = (): void => h.current.onOpenPaymentMethod();

    window.addEventListener('novala:openBasePayModal', handleOpenBasePay);
    window.addEventListener('novala:openTaxModal', handleOpenTax);
    window.addEventListener('novala:openAddPayTypeModal', handleOpenAddPay);
    window.addEventListener('novala:openEditPayTypeModal', handleOpenEditPay);
    window.addEventListener('novala:openStatHolidayModal', handleOpenStatHoliday);
    window.addEventListener('novala:openTimeOffModal', handleOpenTimeOff);
    window.addEventListener('novala:openAddDeductionModal', handleOpenAddDeduction);
    window.addEventListener('novala:openDentalCodeModal', handleOpenDentalCode);
    window.addEventListener('novala:unassignDeductionItem', handleUnassignDeduction);
    window.addEventListener('novala:openPersonalInfoModal', handleOpenPersonalInfo);
    window.addEventListener('novala:openEmergencyContactsModal', handleOpenEmergency);
    window.addEventListener('novala:openEmploymentDetailsModal', handleOpenEmployment);
    window.addEventListener('novala:openPaymentMethodModal', handleOpenPaymentMethod);
    window.addEventListener('novala:payItemsLoaded', handlePayItemsLoaded);

    return () => {
      window.removeEventListener('novala:openBasePayModal', handleOpenBasePay);
      window.removeEventListener('novala:openTaxModal', handleOpenTax);
      window.removeEventListener('novala:openAddPayTypeModal', handleOpenAddPay);
      window.removeEventListener('novala:openEditPayTypeModal', handleOpenEditPay);
      window.removeEventListener('novala:openStatHolidayModal', handleOpenStatHoliday);
      window.removeEventListener('novala:openTimeOffModal', handleOpenTimeOff);
      window.removeEventListener('novala:openAddDeductionModal', handleOpenAddDeduction);
      window.removeEventListener('novala:openDentalCodeModal', handleOpenDentalCode);
      window.removeEventListener('novala:unassignDeductionItem', handleUnassignDeduction);
      window.removeEventListener('novala:openPersonalInfoModal', handleOpenPersonalInfo);
      window.removeEventListener('novala:openEmergencyContactsModal', handleOpenEmergency);
      window.removeEventListener('novala:openEmploymentDetailsModal', handleOpenEmployment);
      window.removeEventListener('novala:openPaymentMethodModal', handleOpenPaymentMethod);
      window.removeEventListener('novala:payItemsLoaded', handlePayItemsLoaded);
    };
  }, []);
}